import {
  Box,
  HStack,
  VStack,
  Text,
  Checkbox,
  Select,
  Button,
  Icon,
  Badge,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react'
import { 
  CheckCircleIcon, 
  StarIcon, 
  RepeatIcon, 
  TimeIcon,
  ViewIcon,
  InfoIcon,
} from '@chakra-ui/icons'

interface RunConfigurationTabProps {
  // Tool states
  enableCilksan: boolean
  enableCilkscale: boolean
  enableProfiler: boolean
  
  // Tool handlers
  onCilksanChange: (enabled: boolean) => void
  onCilkscaleChange: (enabled: boolean) => void
  onProfilerChange: (enabled: boolean) => void
  
  // Profiler settings
  hasMultipleInputs: boolean
  profilerInput: string
  availableInputs: string[]
  onProfilerInputChange: (input: string) => void
  
  // Preset
  preset: 'basic' | 'standard' | 'full'
  onPresetChange: (preset: 'basic' | 'standard' | 'full') => void
  
  // Submit handler
  onRunAnalysis: () => void
  isSubmitting: boolean
  isAuthenticated?: boolean
}

export default function RunConfigurationTab({
  enableCilksan,
  enableCilkscale,
  enableProfiler,
  onCilksanChange,
  onCilkscaleChange,
  onProfilerChange,
  hasMultipleInputs,
  profilerInput,
  availableInputs,
  onProfilerInputChange,
  preset,
  onPresetChange,
  onRunAnalysis,
  isSubmitting,
  isAuthenticated = true,
}: RunConfigurationTabProps) {
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const textColor = useColorModeValue('gray.700', 'gray.200')
  const mutedColor = useColorModeValue('gray.600', 'gray.400')
  const accentBg = useColorModeValue('blue.50', 'blue.900')
  
  // Calculate how many advanced tools are enabled
  const advancedToolsCount = [enableCilksan, enableCilkscale, enableProfiler].filter(Boolean).length
  
  return (
    <Box h="100%" overflow="auto" p={4} display="flex" flexDirection="column" bg={bgColor}>
      <VStack spacing={6} align="stretch" w="100%">
        {/* Header */}
        <Box>
          <Text fontSize="lg" fontWeight="semibold" color={textColor} mb={2}>
            Run Configuration
          </Text>
          <Text fontSize="sm" color={mutedColor}>
            Configure which analysis tools to run with your code submission.
          </Text>
        </Box>

        {/* Core Tests (Always On) */}
        <Box>
          <HStack spacing={3} mb={3}>
            <Icon as={CheckCircleIcon} color="green.500" boxSize={5} />
            <Text fontSize="md" fontWeight="medium" color={textColor}>
              Core Tests
            </Text>
            <Badge colorScheme="green" fontSize="xs">Always On</Badge>
          </HStack>
          <Text fontSize="sm" color={mutedColor} mb={3}>
            Correctness and benchmark tests are always included in every submission.
          </Text>
        </Box>

        <Divider />

        {/* Preset Selector */}
        <Box>
          <Text fontSize="md" fontWeight="medium" color={textColor} mb={3}>
            Analysis Preset
          </Text>
          <Select
            value={preset}
            onChange={(e) => onPresetChange(e.target.value as 'basic' | 'standard' | 'full')}
            fontSize="sm"
            bg={bgColor}
            borderColor={borderColor}
          >
            <option value="basic">Basic - Core tests only</option>
            <option value="standard">Standard - Recommended</option>
            <option value="full">Full Analysis - All tools enabled</option>
          </Select>
          <Text fontSize="xs" color={mutedColor} mt={2}>
            Presets automatically configure the tools below. You can also customize individually.
          </Text>
        </Box>

        <Divider />

        {/* Advanced Analysis Tools */}
        <Box>
          <HStack spacing={2} mb={4}>
            <Text fontSize="md" fontWeight="medium" color={textColor}>
              Advanced Analysis
            </Text>
            {advancedToolsCount > 0 && (
              <Badge colorScheme="purple" fontSize="xs">
                {advancedToolsCount} active
              </Badge>
            )}
          </HStack>
          
          <VStack spacing={4} align="stretch">
            {/* Race Detection */}
            <Box 
              p={3} 
              borderRadius="md" 
              border="1px" 
              borderColor={enableCilksan ? "orange.200" : borderColor}
              bg={enableCilksan ? "orange.50" : "transparent"}
            >
              <HStack justify="space-between" align="flex-start">
                <VStack align="flex-start" spacing={1} flex={1}>
                  <HStack spacing={2}>
                    <Icon as={RepeatIcon} color={enableCilksan ? "orange.500" : "gray.400"} boxSize={4} />
                    <Text fontSize="sm" fontWeight="medium">Race Detection</Text>
                  </HStack>
                  <Text fontSize="xs" color={mutedColor}>
                    Detect data races in parallel code using Cilksan
                  </Text>
                </VStack>
                <Checkbox
                  isChecked={enableCilksan}
                  onChange={(e) => onCilksanChange(e.target.checked)}
                  colorScheme="orange"
                  size="sm"
                />
              </HStack>
            </Box>
            
            {/* Scalability Analysis */}
            <Box 
              p={3} 
              borderRadius="md" 
              border="1px" 
              borderColor={enableCilkscale ? "teal.200" : borderColor}
              bg={enableCilkscale ? "teal.50" : "transparent"}
            >
              <HStack justify="space-between" align="flex-start">
                <VStack align="flex-start" spacing={1} flex={1}>
                  <HStack spacing={2}>
                    <Icon as={StarIcon} color={enableCilkscale ? "teal.500" : "gray.400"} boxSize={4} />
                    <Text fontSize="sm" fontWeight="medium">Scalability Analysis</Text>
                  </HStack>
                  <Text fontSize="xs" color={mutedColor}>
                    Measure parallelism and scalability using Cilkscale
                  </Text>
                </VStack>
                <Checkbox
                  isChecked={enableCilkscale}
                  onChange={(e) => onCilkscaleChange(e.target.checked)}
                  colorScheme="teal"
                  size="sm"
                />
              </HStack>
            </Box>
            
            {/* Performance Profiler */}
            <Box 
              p={3} 
              borderRadius="md" 
              border="1px" 
              borderColor={enableProfiler ? "purple.200" : borderColor}
              bg={enableProfiler ? "purple.50" : "transparent"}
            >
              <HStack justify="space-between" align="flex-start">
                <VStack align="flex-start" spacing={1} flex={1}>
                  <HStack spacing={2}>
                    <Icon as={TimeIcon} color={enableProfiler ? "purple.500" : "gray.400"} boxSize={4} />
                    <Text fontSize="sm" fontWeight="medium">Line Profiler</Text>
                  </HStack>
                  <Text fontSize="xs" color={mutedColor}>
                    Profile performance at the line level
                  </Text>
                </VStack>
                <Checkbox
                  isChecked={enableProfiler}
                  onChange={(e) => onProfilerChange(e.target.checked)}
                  colorScheme="purple"
                  size="sm"
                />
              </HStack>
              
              {/* Profiler Input Selection */}
              {enableProfiler && hasMultipleInputs && (
                <Box mt={3} pt={3} borderTop="1px" borderColor={borderColor}>
                  <Text fontSize="xs" fontWeight="medium" color={textColor} mb={2}>
                    Profiler Input Selection
                  </Text>
                  <Select
                    size="sm"
                    value={profilerInput || ''}
                    onChange={(e) => onProfilerInputChange(e.target.value)}
                    placeholder="All inputs"
                    fontSize="xs"
                    bg={bgColor}
                  >
                    {availableInputs.map((input) => (
                      <option key={input} value={input}>
                        {input}
                      </option>
                    ))}
                  </Select>
                  <HStack spacing={1} mt={2}>
                    <Icon as={InfoIcon} color={mutedColor} boxSize={3} />
                    <Text fontSize="xs" color={mutedColor}>
                      When a specific input is selected, ALL tests run only on that input.
                    </Text>
                  </HStack>
                </Box>
              )}
            </Box>
          </VStack>
        </Box>

        {/* Active Configuration Summary */}
        {(enableCilksan || enableCilkscale || enableProfiler || profilerInput) && (
          <Box
            bg={accentBg}
            px={3}
            py={3}
            borderRadius="md"
            fontSize="xs"
          >
            <Text fontWeight="medium" color={textColor} mb={2}>Active Configuration:</Text>
            <HStack spacing={2} wrap="wrap">
              <Badge colorScheme="green" variant="subtle">Core Tests</Badge>
              <Badge colorScheme="blue" variant="subtle">Benchmark</Badge>
              {enableCilksan && <Badge colorScheme="orange" variant="subtle">Race Detection</Badge>}
              {enableCilkscale && <Badge colorScheme="teal" variant="subtle">Scalability</Badge>}
              {enableProfiler && (
                <Badge colorScheme="purple" variant="subtle">
                  Profiler{profilerInput && `: ${profilerInput}`}
                </Badge>
              )}
            </HStack>
          </Box>
        )}

        {/* Run Button */}
        <Box pt={4}>
          <Button
            size="md"
            colorScheme={isAuthenticated ? "blue" : "gray"}
            leftIcon={<Icon as={ViewIcon} />}
            onClick={onRunAnalysis}
            isLoading={isSubmitting}
            loadingText="Running..."
            width="100%"
            isDisabled={!isAuthenticated}
          >
            {isAuthenticated ? "Run Analysis" : "Login to Run"}
          </Button>
          <Text fontSize="xs" color={mutedColor} mt={2} textAlign="center">
            {isAuthenticated ? "Shortcut: Ctrl+Enter" : "Authentication required"}
          </Text>
        </Box>

        {/* Keyboard Shortcuts Help */}
        <Box>
          <Text fontSize="xs" fontWeight="medium" color={textColor} mb={2}>
            Keyboard Shortcuts:
          </Text>
          <VStack spacing={1} align="stretch" fontSize="xs" color={mutedColor}>
            <HStack justify="space-between">
              <Text>Run Analysis</Text>
              <Text>Ctrl+Enter</Text>
            </HStack>
            <HStack justify="space-between">
              <Text>Toggle Race Detection</Text>
              <Text>Ctrl+Shift+S</Text>
            </HStack>
            <HStack justify="space-between">
              <Text>Toggle Scalability</Text>
              <Text>Ctrl+Shift+C</Text>
            </HStack>
            <HStack justify="space-between">
              <Text>Toggle Profiler</Text>
              <Text>Ctrl+Shift+R</Text>
            </HStack>
          </VStack>
        </Box>
      </VStack>
    </Box>
  )
} 